import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  type ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { PreloadAllModules, provideRouter, Router, withPreloading } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { TranslocoService } from '@jsverse/transloco';
import { CookieService } from 'ngx-cookie-service';
import { provideNgxMask } from 'ngx-mask';
import { firstValueFrom } from 'rxjs';

import { environment } from '../environments/environment';
import { appInitializer } from './app.initializer';
import { appRoutes } from './app.routes';
import { ApiService } from './core/api/api.service';
import { AuthApiService } from './core/api/auth-api.service';
import { CoreService } from './core/api/core.service';
import { errorHandlingInterceptor } from './core/api/interceptors/error-handling.interceptor';
import { tokenInterceptor } from './core/api/interceptors/token.interceptor';
import { Auth0Service } from './core/oauth0';
import {
  CurrentUserService,
  GtmService,
  provideRemoteConfig,
  RemoteConfigService,
  SearchService,
  TenantsService,
  UsersService
} from './core/services';
import { provideTranslocoApp } from './transloco';

export const appConfig: ApplicationConfig = {
  providers: [
    ApiService,
    AuthApiService,
    CookieService,
    CoreService,
    importProvidersFrom(MatNativeDateModule),
    provideTranslocoApp(),
    provideRemoteConfig(),
    SearchService,
    provideAppInitializer(async () => {
      const auth = inject(Auth0Service);
      const currentUser = inject(CurrentUserService);
      const usersService = inject(UsersService);
      const tenantService = inject(TenantsService);
      const gtmService = inject(GtmService);
      const rc = inject(RemoteConfigService);
      const router = inject(Router);

      await rc.initialize();
      const initializerFn = appInitializer(auth, currentUser, usersService, tenantService, gtmService);
      await firstValueFrom(initializerFn());

      if (auth.redirectPath) {
        const path = auth.redirectPath;
        auth.redirectPath = null;
        await router.navigateByUrl(path);
      }
    }),
    provideHttpClient(withInterceptors([tokenInterceptor, errorHandlingInterceptor])),
    provideNgxMask(),
    provideRouter(appRoutes, withPreloading(PreloadAllModules)),
    provideServiceWorker('ngsw-worker.js', { enabled: environment.production }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    {
      provide: LOCALE_ID,
      deps: [TranslocoService],
      useFactory: (translocoService: TranslocoService) => translocoService.getActiveLang()
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { hideRequiredMarker: true } }
  ]
};
